<template>
  <div class="rte">
    <div class="rte-language-tabs" role="tablist" aria-label="Idioma de la plantilla">
      <button
        type="button"
        role="tab"
        class="rte-language-tab"
        :class="{ 'is-active': activeLanguage === 'es' }"
        :aria-selected="activeLanguage === 'es'"
        @click="switchLanguage('es')"
      >
        Español · predeterminado
      </button>
      <button
        type="button"
        role="tab"
        class="rte-language-tab"
        :class="{ 'is-active': activeLanguage === 'en' }"
        :aria-selected="activeLanguage === 'en'"
        @click="switchLanguage('en')"
      >
        English · requested version
      </button>
    </div>

    <p class="rte-language-help">
      <template v-if="activeLanguage === 'es'">
        Esta versión se envía siempre. Si existe contenido en inglés, se agregará al pie un enlace para solicitarlo.
      </template>
      <template v-else>
        This version is sent only when the recipient requests it from the link in the original email.
      </template>
    </p>

    <!-- Asunto -->
    <div class="rte-field">
      <label class="rte-label" for="asunto">
        {{ activeLanguage === 'es' ? 'Asunto personalizado' : 'English subject' }}
      </label>
      <input
        id="asunto"
        ref="subjectEl"
        v-model="subject"
        type="text"
        class="rte-input"
        :placeholder="activeLanguage === 'es'
          ? 'Ej: Invitación a {{NOMBRE_EVENTO}} 2025'
          : 'Example: Invitation to {{NOMBRE_EVENTO}} 2025'"
        @focus="subjectHasFocus = true"
        @blur="subjectHasFocus = false"
        @keyup="saveSubjectCaret"
        @mouseup="saveSubjectCaret"
      />
    </div>

    <!-- Variables -->
    <div class="rte-toolbar">
      <span class="rte-label">Variables:</span>
      <button
        v-for="v in variablesDisponibles"
        :key="v.clave"
        type="button"
        class="rte-btn"
        @mousedown.stop.prevent="cacheCaretBeforeClick"
        @click="insertarVariable(v.clave)"
      >
        {{ v.clave }}
      </button>
    </div>

    <!-- Toolbar de formato -->
    <div class="rte-toolbar">
      <select class="rte-select" v-model="blockFormat" @change="applyBlockFormat">
        <option value="p">Párrafo</option>
        <option value="h1">Encabezado 1</option>
        <option value="h2">Encabezado 2</option>
        <option value="h3">Encabezado 3</option>
      </select>

      <select class="rte-select" v-model="fontFamily" @change="applyFontFamily">
        <option value="">Fuente</option>
        <option value="Helvetica, Arial, sans-serif">Helvetica / Arial</option>
        <option value="Verdana, Geneva, sans-serif">Verdana</option>
        <option value="Georgia, serif">Georgia</option>
        <option value="'Times New Roman', Times, serif">Times New Roman</option>
        <option value="'Courier New', Courier, monospace">Courier New</option>
      </select>

      <button type="button" class="rte-btn" @mousedown.prevent @click="cmd('bold')">B</button>
      <button type="button" class="rte-btn" @mousedown.prevent @click="cmd('italic')"><i>I</i></button>
      <button type="button" class="rte-btn" @mousedown.prevent @click="cmd('underline')"><u>U</u></button>

      <button type="button" class="rte-btn" @mousedown.prevent @click="cmd('insertUnorderedList')">• Lista</button>
      <button type="button" class="rte-btn" @mousedown.prevent @click="cmd('insertOrderedList')">1. Lista</button>

      <!-- Alineación -->
      <button type="button" class="rte-btn" title="Alinear izquierda" @mousedown.prevent @click="align('left')">⟸</button>
      <button type="button" class="rte-btn" title="Centrar"           @mousedown.prevent @click="align('center')">⟂</button>
      <button type="button" class="rte-btn" title="Alinear derecha"   @mousedown.prevent @click="align('right')">⟹</button>
      <button type="button" class="rte-btn" title="Justificar"        @mousedown.prevent @click="align('justify')">≡</button>

      <button type="button" class="rte-btn" @mousedown.prevent @click="removeFormatting">Limpiar</button>
    </div>

    <!-- Editor -->
    <div
      ref="editorEl"
      class="rte-editor"
      contenteditable="true"
      role="textbox"
      aria-multiline="true"
      @input="syncHtml"
      @keyup="saveRange"
      @mouseup="saveRange"
      @blur="saveRange"
      @focus="restoreRange"
    ></div>

    <div class="rte-actions">
      <button class="rte-save" @click="saveContent">Guardar plantilla bilingüe</button>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, defineProps, nextTick } from 'vue'

const props = defineProps({
  nombreEvento: String,
  tokenEvento: String
})

/* -------- estado -------- */
const editorEl = ref(null)
const subjectEl = ref(null)

const activeLanguage = ref('es')
const htmlEs = ref('')
const htmlEn = ref('')
const subjectEs = ref('')
const subjectEn = ref('')
const html = computed({
  get: () => activeLanguage.value === 'en' ? htmlEn.value : htmlEs.value,
  set: value => {
    if (activeLanguage.value === 'en') htmlEn.value = value
    else htmlEs.value = value
  }
})
const subject = computed({
  get: () => activeLanguage.value === 'en' ? subjectEn.value : subjectEs.value,
  set: value => {
    if (activeLanguage.value === 'en') subjectEn.value = value
    else subjectEs.value = value
  }
})
let subjectHasFocus = false
let subjectCaretPos = 0          // posición del caret en el input

/* Selección/caret editor */
let lastRange = null
let clickRange = null

/* Toolbar */
const blockFormat = ref('p')
const fontFamily  = ref('')

/* variables */
const variablesDisponibles = [
  { clave: '{{NOMBRE_COMPLETO}}' },
  { clave: '{{NUMERO_DE_DOCUMENTO}}' },
  { clave: '{{CORREO_ELECTRONICO}}' },
  { clave: '{{NOMBRE_EVENTO}}' },
  { clave: '{{NOMBRE_GRUPO}}' }
]

/* -------- utils selección editor -------- */
function getSelectionRange() {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  const rng = sel.getRangeAt(0)
  const host = editorEl.value
  if (!host) return null
  const within = rng.commonAncestorContainer === host || host.contains(rng.commonAncestorContainer)
  return within ? rng : null
}

function saveRange() {
  const rng = getSelectionRange()
  if (rng) lastRange = rng
  syncBlockFormatSelector()
}

function restoreRange() {
  if (!lastRange) return
  const sel = window.getSelection()
  if (!sel) return
  try { sel.removeAllRanges(); sel.addRange(lastRange) } catch {console.log('')}
}

function focusEditor() { editorEl.value?.focus() }

/* -------- asunto: caret y helpers -------- */
function saveSubjectCaret() {
  if (!subjectEl.value) return
  subjectCaretPos = subjectEl.value.selectionStart || 0
}

function insertIntoSubject(text) {
  const el = subjectEl.value
  if (!el) return
  const start = el.selectionStart ?? subjectCaretPos ?? 0
  const end   = el.selectionEnd   ?? subjectCaretPos ?? start
  const before = subject.value.slice(0, start)
  const after  = subject.value.slice(end)
  subject.value = before + text + after
  // recoloca caret después de lo insertado
  const newPos = start + text.length
  requestAnimationFrame(() => {
    el.focus()
    el.setSelectionRange(newPos, newPos)
    subjectCaretPos = newPos
  })
}

/* -------- sync HTML editor -------- */
function syncHtml() {
  html.value = editorEl.value?.innerHTML || ''
}

async function switchLanguage(language) {
  if (language === activeLanguage.value) return

  syncHtml()
  activeLanguage.value = language
  subjectHasFocus = false
  subjectCaretPos = 0
  lastRange = null
  clickRange = null

  await nextTick()
  if (editorEl.value) {
    editorEl.value.innerHTML = html.value || ''
    editorEl.value.style.textAlign = 'left'
  }
}

/* -------- comandos base -------- */
function cmd(name) {
  focusEditor()
  try { document.execCommand('styleWithCSS', false, true) } catch {console.log('')}
  try { document.execCommand(name, false, null) } catch {console.log('')}
  syncHtml()
  saveRange()
}

/* -------- encabezados -------- */
function applyBlockFormat() {
  focusEditor()
  const tag = (blockFormat.value || 'p').toUpperCase()
  try {
    document.execCommand('formatBlock', false, tag)
  } catch {
    wrapSelectionWithTag(tag.toLowerCase())
  }
  syncHtml()
  saveRange()
}

function wrapSelectionWithTag(tagName) {
  focusEditor()
  const rng = getSelectionRange() || lastRange
  if (!rng || !editorEl.value) return

  const sel = window.getSelection()
  const tag = String(tagName || 'p').toLowerCase()
  const el  = document.createElement(tag)

  try {
    if (rng.collapsed) {
      el.appendChild(document.createTextNode('\u200B'))
      rng.insertNode(el)
      const newRange = document.createRange()
      newRange.setStart(el.firstChild, 1)
      newRange.setEnd(el.firstChild, 1)
      sel.removeAllRanges()
      sel.addRange(newRange)
      lastRange = newRange
    } else {
      const frag = rng.extractContents()
      el.appendChild(frag)
      rng.insertNode(el)
      const newRange = document.createRange()
      newRange.selectNodeContents(el)
      newRange.collapse(false)
      sel.removeAllRanges()
      sel.addRange(newRange)
      lastRange = newRange
    }
  } catch {
    insertHTML(`<${tag}>${escapeHtml(rng.toString())}</${tag}>`, true)
  }
}

/* -------- fuente -------- */
function applyFontFamily() {
  if (!fontFamily.value) return
  applyInlineStyle({ 'font-family': fontFamily.value })
}
function applyInlineStyle(styleObj) {
  focusEditor()
  const rng = getSelectionRange() || lastRange
  if (!rng) { insertHTML(`<span style="${styleToString(styleObj)}"></span>`); return }
  if (rng.collapsed) { insertHTML(`<span style="${styleToString(styleObj)}">\u200B</span>`); return }
  try {
    const span = document.createElement('span')
    Object.entries(styleObj).forEach(([k, v]) => span.style.setProperty(k, v))
    rng.surroundContents(span)
  } catch {
    const selected = rng.toString()
    insertHTML(`<span style="${styleToString(styleObj)}">${escapeHtml(selected)}</span>`, true)
  }
  syncHtml()
  saveRange()
}
function styleToString(obj) { return Object.entries(obj).map(([k, v]) => `${k}:${v}`).join(';') }

/* -------- alineación -------- */
function align(pos) {
  focusEditor()
  const map = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight', justify: 'justifyFull' }
  try { document.execCommand(map[pos] || 'justifyLeft', false, null) } catch {console.log('')}
  setBlockStyle('text-align', pos === 'justify' ? 'justify' : pos)
  syncHtml()
  saveRange()
}
function setBlockStyle(prop, value) {
  const rng = getSelectionRange() || lastRange
  if (!rng) return
  let node = rng.commonAncestorContainer
  if (node.nodeType === 3) node = node.parentNode
  const host = editorEl.value
  while (node && node !== host) {
    if (/^(P|DIV|H1|H2|H3|LI)$/i.test(node.tagName)) {
      node.style.setProperty(prop, value)
      return
    }
    node = node.parentNode
  }
}

/* -------- inserción HTML/texto editor -------- */
function insertHTML(htmlStr, replaceSelection = false) {
  focusEditor()
  const rng = getSelectionRange() || lastRange
  if (!rng) {
    editorEl.value?.insertAdjacentHTML('beforeend', htmlStr)
    placeCaretAtEnd(editorEl.value)
    syncHtml()
    return
  }
  const sel = window.getSelection()
  if (!sel) return
  if (replaceSelection && !rng.collapsed) rng.deleteContents()

  const temp = document.createElement('div')
  temp.innerHTML = htmlStr
  const frag = document.createDocumentFragment()
  let node, lastNode
  while ((node = temp.firstChild)) lastNode = frag.appendChild(node)
  rng.insertNode(frag)

  if (lastNode) {
    rng.setStartAfter(lastNode)
    rng.setEndAfter(lastNode)
    sel.removeAllRanges(); sel.addRange(rng)
    lastRange = rng
  }
  syncHtml()
}
function insertTextAtCaret(text) { insertHTML(escapeHtml(text), true) }
function placeCaretAtEnd(el) {
  el.focus()
  const range = document.createRange()
  range.selectNodeContents(el); range.collapse(false)
  const sel = window.getSelection()
  if (!sel) return
  sel.removeAllRanges(); sel.addRange(range)
  lastRange = range
}
function escapeHtml(s) { return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;') }

/* -------- limpieza -------- */
function removeFormatting() {
  focusEditor()
  try { document.execCommand('removeFormat', false, null) } catch {console.log('')}
  stripInlineStylesInSelection()
  syncHtml()
  saveRange()
}
function stripInlineStylesInSelection() {
  const host = editorEl.value
  if (!host) return
  const selection = getSelectionRange()
  const scope = selection ? selection.commonAncestorContainer : host
  const root = (scope.nodeType === 1 ? scope : scope.parentNode)
  const spans = root.querySelectorAll('span[style]')
  spans.forEach(sp => sp.removeAttribute('style'))
}

/* -------- variables: detecta destino (asunto vs editor) -------- */
function cacheCaretBeforeClick() {
  const rng = getSelectionRange()
  clickRange = rng || lastRange
  saveSubjectCaret()
}
function insertarVariable(txt) {
  // Si el input de asunto tiene el foco, inserta ahí
  if (subjectHasFocus && document.activeElement === subjectEl.value) {
    insertIntoSubject(txt)
    return
  }
  // Si no, al editor
  focusEditor()
  const sel = window.getSelection()
  if (!sel) return
  let rng = clickRange || getSelectionRange() || lastRange
  if (!rng) { insertTextAtCaret(txt); return }
  try { sel.removeAllRanges(); sel.addRange(rng) } catch {console.log('')}
  insertTextAtCaret(txt)
  saveRange()
  clickRange = null
}

/* -------- selector de bloque ↔ selección -------- */
function syncBlockFormatSelector() {
  const rng = getSelectionRange() || lastRange
  if (!rng) return
  let node = rng.commonAncestorContainer
  if (node.nodeType === 3) node = node.parentNode
  const host = editorEl.value
  while (node && node !== host) {
    const tag = (node.tagName || '').toLowerCase()
    if (['p','h1','h2','h3'].includes(tag)) {
      blockFormat.value = tag
      return
    }
    node = node.parentNode
  }
  blockFormat.value = 'p'
}

/* -------- sanitización --------
   - En asunto: texto plano (sin HTML) → quitamos etiquetas por seguridad
   - En HTML: permitimos estilos básicos necesarios (peso/estilo/subrayado/alineación/familia)
*/
function stripTags(str) {
  return String(str).replace(/<\/?[^>]+(>|$)/g, '')
}

function sanitizeHtml(raw) {
  const allowedTags = new Set(['P','H1','H2','H3','STRONG','B','EM','I','U','BR','UL','OL','LI','A','SPAN','DIV'])
  const allowedAttrs = {
    'A': new Set(['href','target','rel']),
    'SPAN': new Set(['style']),
    'DIV': new Set(['style']),
    'P': new Set(['style']),
    'H1': new Set(['style']),
    'H2': new Set(['style']),
    'H3': new Set(['style'])
  }
  const allowedStyles = new Set(['text-align','font-family','font-weight','font-style','text-decoration'])

  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html')
  const root = doc.body.firstChild

  const walk = node => {
    if (node.nodeType === 1) {
      const tag = node.tagName
      if (!allowedTags.has(tag)) {
        const parent = node.parentNode
        while (node.firstChild) parent.insertBefore(node.firstChild, node)
        parent.removeChild(node)
        return
      }
      for (const attr of Array.from(node.attributes)) {
        const tagAllow = allowedAttrs[tag]
        if (!tagAllow || !tagAllow.has(attr.name)) {
          node.removeAttribute(attr.name)
          continue
        }
        if (attr.name === 'style') {
          const style = node.getAttribute('style') || ''
          const filtered = style.split(';').map(s=>s.trim()).filter(Boolean).filter(rule=>{
            const [prop] = rule.split(':').map(x=>x.trim().toLowerCase())
            return allowedStyles.has(prop)
          }).join('; ')
          if (filtered) node.setAttribute('style', filtered); else node.removeAttribute('style')
        }
        if (tag === 'A' && attr.name === 'href') {
          const href = node.getAttribute('href') || ''
          if (/^\s*javascript:/i.test(href)) node.removeAttribute('href')
        }
        if (tag === 'A' && attr.name === 'target') {
          node.setAttribute('rel','noopener noreferrer')
        }
      }
    }
    for (const child of Array.from(node.childNodes)) walk(child)
  }
  walk(root)
  return root.innerHTML
}

/* -------- guardar -------- */
async function saveContent() {
  try {
    syncHtml()
    const payload = {
      token_evento: props.tokenEvento,
      nombre_evento: props.nombreEvento,
      asunto_personalizado: stripTags(subjectEs.value || ''),
      contenido_html: sanitizeHtml(htmlEs.value || ''),
      asunto_personalizado_en: stripTags(subjectEn.value || ''),
      contenido_html_en: sanitizeHtml(htmlEn.value || '')
    }
    const res = await fetch('/api/mensaje-personalizado', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'No fue posible guardar la plantilla')

    htmlEs.value = payload.contenido_html
    htmlEn.value = payload.contenido_html_en
    alert(data.status || 'Plantilla guardada correctamente')
  } catch (e) {
    alert('Error al guardar: ' + (e?.message || e))
  }
}

/* -------- carga inicial -------- */
onMounted(async () => {
  try { document.execCommand('defaultParagraphSeparator', false, 'p') } catch {console.log('')}

  try {
    const r = await fetch(`/api/mensaje-personalizado/${props.tokenEvento}`)
    const data = await r.json()
    htmlEs.value = (data?.status === 'ok' && data?.data?.contenido_html) ? String(data.data.contenido_html) : ''
    subjectEs.value = (data?.status === 'ok' && data?.data?.asunto_personalizado) ? String(data.data.asunto_personalizado) : ''
    htmlEn.value = (data?.status === 'ok' && data?.data?.contenido_html_en) ? String(data.data.contenido_html_en) : ''
    subjectEn.value = (data?.status === 'ok' && data?.data?.asunto_personalizado_en) ? String(data.data.asunto_personalizado_en) : ''
  } catch {
    htmlEs.value = ''
    subjectEs.value = ''
    htmlEn.value = ''
    subjectEn.value = ''
  }

  await nextTick()
  if (editorEl.value) {
    editorEl.value.innerHTML = html.value || ''
    editorEl.value.style.textAlign = 'left'
    placeCaretAtEnd(editorEl.value)
  }

  document.addEventListener('selectionchange', selectionChangeHandler, true)
})

function selectionChangeHandler() {
  const rng = getSelectionRange()
  if (rng) {
    lastRange = rng
    syncBlockFormatSelector()
  }
}

onBeforeUnmount(() => {
  document.removeEventListener('selectionchange', selectionChangeHandler, true)
})
</script>

<style scoped>
/* Layout / campos */
.rte { display: grid; gap: 10px; }
.rte-field { display: grid; gap: 6px; }
.rte-label { font-size: 0.875rem; color: #374151; }
.rte-language-tabs {
  display: inline-flex; flex-wrap: wrap; gap: 4px; width: fit-content;
  padding: 4px; border: 1px solid #dbe3ef; border-radius: 10px; background: #f8fafc;
}
.rte-language-tab {
  border: 0; border-radius: 7px; padding: 8px 12px; background: transparent;
  color: #475569; font: inherit; font-weight: 600; cursor: pointer;
}
.rte-language-tab.is-active { background: #2563eb; color: #fff; box-shadow: 0 1px 3px rgba(15, 23, 42, .18); }
.rte-language-help { margin: 0; color: #64748b; font-size: .875rem; }
.rte-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font: inherit;
  background: #fff;
}

/* Controles */
.rte-toolbar { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.rte-btn {
  font: inherit; padding: 6px 10px; border-radius: 6px;
  border: 1px solid #e5e7eb; background: #f3f4f6; cursor: pointer;
}
.rte-btn:hover { background: #e5e7eb; }
.rte-select {
  padding: 6px 10px; border-radius: 6px; border: 1px solid #e5e7eb; background: white; font: inherit;
}

/* Editor */
.rte-editor {
  min-height: 300px; padding: 12px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: #fff; line-height: 1.5; outline: none; text-align: left !important; white-space: normal;
  font-family: Helvetica, Arial, sans-serif; font-size: 14px;
}

/* Headings visibles en el editor (usar :deep por el scope) */
:deep(.rte-editor h1) { font-size: 36px; line-height: 1.22; font-weight: 700; margin: 0 0 10px; }
:deep(.rte-editor h2) { font-size: 28px; line-height: 1.28; font-weight: 700; margin: 0 0 10px; }
:deep(.rte-editor h3) { font-size: 22px; line-height: 1.32; font-weight: 700; margin: 0 0 10px; }

:deep(.rte-editor p),
:deep(.rte-editor ul),
:deep(.rte-editor ol),
:deep(.rte-editor li) { margin: 0 0 8px; }

:deep(.rte-editor ul) { list-style: disc; padding-left: 1.25rem; }
:deep(.rte-editor ol) { list-style: decimal; padding-left: 1.25rem; }

:deep(.rte-editor strong), :deep(.rte-editor b) { font-weight: 700; }
:deep(.rte-editor em), :deep(.rte-editor i) { font-style: italic; }
:deep(.rte-editor u) { text-decoration: underline; }

/* Actions */
.rte-actions { display: flex; justify-content: flex-start; }
.rte-save {
  background: #3b82f6; color: white; border: none; border-radius: 8px;
  padding: 10px 14px; cursor: pointer;
}
.rte-save:hover { background: #2563eb; }
</style>
